import type { Location } from 'slate';
import { Editor, Node, Text } from 'slate';

import { mergeSuggestedEdits } from './suggested-edits';
import { mergeWithDefaults, ptToPx } from '../../util';
import { timesNewRomanDefault } from '../defaults/font-types';
import type {
  AlineFontName,
  AlineText,
  TextProps,
  TextPropsWithoutComments,
  VerticalAlign,
} from '../internal';
import {
  AlineTransforms,
  defaultFontSize,
  EditingMode,
  isAlineFontName,
  isBoldEdit,
  isFontColorEdit,
  isFontSizeEdit,
  isFontTypeEdit,
  isHighlightEdit,
  isItalicEdit,
  isSmallCapsEdit,
  isStrikethroughEdit,
  isUnderlineEdit,
  isVerticalAlignEdit,
  Marks,
  Queries,
  subscriptOffsetCoefficient,
  superscriptOffsetCoefficient,
  superscriptSubscriptFontSizeCoefficient,
} from '../internal';

export interface LeafFontStyle {
  fontSizePx: number;
  fontStyle: 'italic' | 'normal';
  fontType: string;
  fontWeight: 'bold' | 'normal';
  /** OpenType `smcp` via canvas `fontVariantCaps`, when the font and rasterizer support it. */
  fontVariantCaps?: 'small-caps';
}

export const BLACK_COLOR = 'rgb(0,0,0)';

export const DEFAULT_FONT_TYPE = timesNewRomanDefault.fontFamily;

const leafQueries = {
  activeFontFamily: (textNode: AlineText): AlineText['fontType']['fontFamily'] => {
    const fontTypeEdit = textNode.suggestedEdits?.find(isFontTypeEdit);
    if (fontTypeEdit) return fontTypeEdit.value.fontType.fontFamily;

    const fontType = textNode['fontType'];
    if (!fontType) return DEFAULT_FONT_TYPE;

    return fontType.fontFamily;
  },
  activeFontSizePt: (
    textProps: Partial<TextPropsWithoutComments>
  ): AlineText['fontSize']['size'] => {
    const fontSizeEdit = textProps.suggestedEdits?.find(isFontSizeEdit);
    if (fontSizeEdit) return fontSizeEdit.value.fontSize.size;

    const fontSize = textProps['fontSize'];

    return fontSize?.size ?? defaultFontSize;
  },

  activeFontSizePx: (
    textProps: Partial<TextPropsWithoutComments>
  ): AlineText['fontSize']['size'] => {
    return ptToPx(Queries.activeFontSizePt(textProps));
  },

  activeFontType: (textProps: Partial<TextPropsWithoutComments>): AlineText['fontType']['type'] => {
    const fontTypeEdit = textProps.suggestedEdits?.find(isFontTypeEdit);
    if (fontTypeEdit) return fontTypeEdit.value.fontType.type;

    return textProps.fontType?.type ?? timesNewRomanDefault.type;
  },

  activeVerticalAlign: (textProps: Partial<TextPropsWithoutComments>): VerticalAlign => {
    const verticalAlignEdit = textProps.suggestedEdits?.find(isVerticalAlignEdit);
    if (verticalAlignEdit) return verticalAlignEdit.value.verticalAlign?.align || 'baseline';

    return textProps.verticalAlign?.align ?? 'baseline';
  },

  /**
   * Given an AlineText's props, returns TextProps with suggestedEdits "accepted".
   */
  getActiveTextProps<T extends Partial<AlineText> = Partial<AlineText>>(
    textProps: T
  ): Omit<T, 'comments' | 'text'> {
    // To be safe, omit text/comments key since an incoming object may still have text on it.
    const { comments, text, ...rest } = textProps;
    if (!Array.isArray(rest.suggestedEdits) || !rest.suggestedEdits?.length)
      return mergeWithDefaults(rest, Marks.default);

    const { suggestedProps } = Queries.getSuggestedTextProps(textProps);
    return mergeWithDefaults({ ...rest, ...suggestedProps }, Marks.default);
  },

  /**
   * Get fallback text props based on Location.
   */
  getFallbackTextProps(
    editor: Editor,
    options: {
      at: Location;
      editingMode: EditingMode;
    }
  ): TextProps {
    const { at, editingMode } = options;
    const styleSheet = Queries.activeStyleSheet(editor);
    const point =
      editingMode === EditingMode.SUGGESTING ? Editor.end(editor, at) : Editor.start(editor, at);
    const { text, ...textProps } = Node.leaf(editor, point.path);
    const normalStyles = styleSheet['style-normal'].styles;

    return { ...normalStyles, ...textProps, comments: [], suggestedEdits: [] };
  },

  /**
   * This function is extracted out so it can be used by measure text and the normalization.
   * It returns the minimal CSS styles relevant for measure text.
   */
  getLeafFontStyle(textNode: AlineText): LeafFontStyle {
    const { text, ...textFormats } = textNode;
    const fontType = Queries.activeFontType(textNode);
    const boldActive = Queries.isBoldActive(textFormats);
    const italicActive = Queries.isItalicActive(textFormats);
    const fontSizePx = Queries.renderedFontSize(textFormats);

    return {
      fontSizePx,
      fontStyle: italicActive ? 'italic' : 'normal',
      fontType,
      fontWeight: boldActive ? 'bold' : 'normal',
    };
  },

  /**
   * Get suggested text props that are stored in suggestedEdits.
   */
  getSuggestedTextProps(textProps: Partial<TextProps>) {
    return mergeSuggestedEdits(textProps.suggestedEdits ?? []);
  },

  /**
   * Get unique font types used in a document.
   */
  getUniqueFontTypes(editor: Editor): Set<AlineFontName> {
    const uniqueFontTypes = new Set<AlineFontName>();
    const listMarkers = AlineTransforms.createPathToListMarker(editor);
    const textEntries = [...Editor.nodes(editor, { at: [], match: Text.isText, voids: true })];

    textEntries.forEach(([text]) => {
      const activeFontType = Queries.activeFontType(text);
      if (isAlineFontName(activeFontType)) uniqueFontTypes.add(activeFontType);
    });
    Object.values(listMarkers).forEach(listMarker => {
      const activeFontType = Queries.activeFontType(listMarker);
      if (isAlineFontName(activeFontType)) uniqueFontTypes.add(activeFontType);
    });

    return uniqueFontTypes;
  },

  isBoldActive: (textProps: Partial<TextPropsWithoutComments>): boolean => {
    const boldEdit = textProps.suggestedEdits?.find(edit => isBoldEdit(edit));
    if (boldEdit && isBoldEdit(boldEdit)) return boldEdit.value.bold.active;

    const bold = textProps['bold'];
    if (!bold) return false;

    return bold.active;
  },

  isItalicActive: (textProps: Partial<TextPropsWithoutComments>): boolean => {
    const italicEdit = textProps.suggestedEdits?.find(isItalicEdit);
    if (italicEdit) return italicEdit.value.italic.active;

    const italic = textProps['italic'];
    if (!italic) return false;

    return italic.active;
  },

  isSmallCapsActive: (textProps: Partial<TextPropsWithoutComments>): boolean => {
    const smallCapsEdit = textProps.suggestedEdits?.find(isSmallCapsEdit);
    if (smallCapsEdit) return smallCapsEdit.value.smallCaps.active;

    const smallCaps = textProps['smallCaps'];
    if (!smallCaps) return false;

    return smallCaps.active;
  },

  isStrikethroughActive: (textProps: Partial<TextPropsWithoutComments>): boolean => {
    const strikethroughEdit = textProps.suggestedEdits?.find(isStrikethroughEdit);
    if (strikethroughEdit) return strikethroughEdit.value.strikethrough.active;

    const strikethrough = textProps['strikethrough'];
    if (!strikethrough) return false;

    return strikethrough.active;
  },

  isUnderlineActive: (textProps: Partial<TextPropsWithoutComments>): boolean => {
    const underlineEdit = textProps.suggestedEdits?.find(isUnderlineEdit);
    if (underlineEdit) return underlineEdit.value.underline.active;

    const underline = textProps['underline'];
    if (!underline) return false;

    return underline.active;
  },

  renderedCommentIds({ comments }: Pick<AlineText, 'comments'>): string[] {
    // Old/imported documents may have `comments: null` on text nodes.
    return Array.isArray(comments) ? comments : [];
  },

  renderedFontColor: (
    textProps: Partial<TextPropsWithoutComments>
  ): AlineText['fontColor']['color'] => {
    const fontColorEdit = textProps.suggestedEdits?.find(isFontColorEdit);
    if (fontColorEdit) return fontColorEdit.value.fontColor.color;

    const fontColor = textProps['fontColor'];

    return fontColor?.color ?? BLACK_COLOR;
  },

  renderedFontSize: (text: Partial<Omit<AlineText, 'text' | 'comments'>>): number => {
    const baseFontSize = Queries.activeFontSizePx(text);

    const verticalAlign = Queries.activeVerticalAlign(text);
    const isSubscriptOrSuperscript = verticalAlign !== 'baseline';

    const fontSize = isSubscriptOrSuperscript
      ? Math.max(Math.round(baseFontSize * superscriptSubscriptFontSizeCoefficient), 1)
      : baseFontSize;

    return fontSize;
  },

  renderedHighlightColor: (
    textProps: Partial<TextPropsWithoutComments>
  ): AlineText['highlight']['color'] => {
    const fontHighlightEdit = textProps.suggestedEdits?.find(isHighlightEdit);
    if (fontHighlightEdit) return fontHighlightEdit.value.highlight.color;

    const fontHighlight = textProps['highlight'];
    if (!fontHighlight) return null;

    return fontHighlight.color;
  },

  renderedVerticalAlignOffset: (textProps: Omit<AlineText, 'comments' | 'text'>): number => {
    const baseFontSize = Queries.activeFontSizePx(textProps);

    const verticalAlign = Queries.activeVerticalAlign(textProps);
    const isSuperscript = verticalAlign === 'superscript';
    const isSubscript = verticalAlign === 'subscript';

    if (!isSubscript && !isSuperscript) return 0;

    return isSuperscript
      ? -(baseFontSize * superscriptOffsetCoefficient)
      : baseFontSize * subscriptOffsetCoefficient;
  },
};

export default leafQueries;
